import React, { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Briefcase, FileSignature, Clock,
  Receipt, TrendingUp, Settings as SettingsIcon, LogOut, Menu, Square, Timer as TimerIcon, Sun, Moon, CalendarDays,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useTimer } from "@/context/TimerContext";
import { useTheme } from "@/context/ThemeContext";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, key: "dashboard" },
  { to: "/clients", label: "Clients", icon: Users, key: "clients" },
  { to: "/jobs", label: "Jobs", icon: Briefcase, key: "jobs" },
  { to: "/agreements", label: "Agreements", icon: FileSignature, key: "agreements" },
  { to: "/timesheets", label: "Timesheets", icon: Clock, key: "timesheets" },
  { to: "/schedule", label: "Schedule", icon: CalendarDays, key: "schedule" },
  { to: "/invoices", label: "Invoices", icon: Receipt, key: "invoices" },
  { to: "/forecast", label: "Forecast", icon: TrendingUp, key: "forecast" },
];

const NavItems = ({ onNavigate }) => (
  <nav className="flex flex-col gap-1">
    {NAV.map((item) => (
      <NavLink
        key={item.key}
        to={item.to}
        end={item.end}
        onClick={onNavigate}
        data-testid={`nav-item-${item.key}`}
        className={({ isActive }) =>
          `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150 ${
            isActive
              ? "bg-accent text-accent-foreground border-l-2 border-primary"
              : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
          }`
        }
      >
        <item.icon className="h-[18px] w-[18px]" />
        {item.label}
      </NavLink>
    ))}
  </nav>
);

const Brand = () => (
  <div className="flex items-center gap-2.5 px-2 py-1">
    <div className="h-9 w-9 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold font-display">O</div>
    <div className="leading-tight">
      <div className="font-semibold font-display text-[15px]">Command Center</div>
      <div className="text-[11px] text-muted-foreground">Freelancer OS</div>
    </div>
  </div>
);

const TimerPill = () => {
  const { active, fmtElapsed, clockOut } = useTimer();
  if (!active) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-1.5 shadow-sm" data-testid="header-timer-pill">
      <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
      <span className="hidden sm:inline text-xs text-muted-foreground max-w-[120px] truncate">{active.job_title}</span>
      <span className="font-mono text-sm font-semibold tabular-nums">{fmtElapsed()}</span>
      <Button size="sm" variant="destructive" className="h-7 px-2" data-testid="header-clock-out-button" onClick={clockOut}>
        <Square className="h-3 w-3 mr-1" /> Stop
      </Button>
    </div>
  );
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const SidebarInner = ({ onNavigate }) => (
    <div className="flex h-full flex-col">
      <div className="h-16 flex items-center border-b px-3"><Brand /></div>
      <div className="flex-1 overflow-y-auto p-3"><NavItems onNavigate={onNavigate} /></div>
      <div className="border-t p-3 space-y-1">
        <NavLink
          to="/settings"
          onClick={onNavigate}
          data-testid="nav-item-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-accent/60"
            }`
          }
        >
          <SettingsIcon className="h-[18px] w-[18px]" /> Settings
        </NavLink>
        <div className="flex items-center gap-2 rounded-lg px-3 py-2">
          <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-semibold">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid="logout-button" onClick={logout} aria-label="Log out">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:w-64 border-r bg-card z-30" data-testid="app-sidebar">
        <SidebarInner />
      </aside>

      <div className="md:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-20 h-16 border-b bg-background/95 backdrop-blur flex items-center gap-3 px-4 sm:px-6">
          <div className="md:hidden">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" data-testid="mobile-nav-open-button" aria-label="Open menu">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <SidebarInner onNavigate={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>
          </div>
          <div className="md:hidden"><Brand /></div>
          <div className="flex-1" />
          <TimerPill />
          <Button variant="outline" size="icon" data-testid="theme-toggle-button" aria-label="Toggle theme" onClick={toggle}>
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <Button className="hidden sm:inline-flex" data-testid="topbar-new-invoice-button" onClick={() => navigate("/invoices?new=1")}>
            <Receipt className="h-4 w-4 mr-2" /> New Invoice
          </Button>
        </header>

        <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
